import { SkupremeModal } from '@components/index';
import { HoverBorderButton, MuteText, StyledButton } from '@elements/index';
import { useState } from 'react';
import { Form, TextArea } from 'semantic-ui-react';
import styled from 'styled-components';
import { theme, ToastMessage, TrackingService } from '@utility/index';

interface AcknowledgeModalProps {
  open: boolean;
  onClose: () => void;
  tracking: TrackingItem;
  onSuccess?: () => void;
}

interface TrackingItem {
  trackingNumber: string;
  carrier: string;
  status: string;
  message?: string;
  order?: {
    marketplaceOrderDisplayId?: string;
  };
  [key: string]: any;
}

const Row = styled.div`
  display: flex;
  column-gap: 7px;
`;

const Banner = styled.div<{ color: string }>`
  background-color: ${props => props.color}10;
  color: ${props => props.color};
  padding: 8px 12px;
  border-radius: 4px;
  display: inline-block;
  margin-bottom: 16px;
`;

export function AcknowledgeModal({
  open,
  onClose,
  tracking,
  onSuccess,
}: AcknowledgeModalProps): JSX.Element {
  const [note, setNote] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleAcknowledge = async () => {
    setLoading(true);

    try {
      await TrackingService.acknowledgeTrackingAlert(
        tracking.carrier,
        tracking.trackingNumber,
        { note: note || undefined },
      );

      ToastMessage.success('Tracking alert acknowledged successfully');
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      ToastMessage.apiError(err);
    } finally {
      setLoading(false);
    }
  };

  const orderDisplayId = tracking.order?.marketplaceOrderDisplayId || 'N/A';

  const colorMap = {
    alerts: theme.colors.red,
    in_process_of_delivery: theme.colors.darkBlue,
  };

  return (
    <SkupremeModal
      title="Acknowledge Tracking Alert"
      open={open}
      onClose={onClose}
      size="small"
    >
      <Banner color={colorMap[tracking.category] || theme.colors.primary}>
        <span>{tracking.message}</span>
      </Banner>
      <div className="row mb-2">
        <Row className="col-6 pl-0">
          <MuteText>Order</MuteText>
          <span>{orderDisplayId}</span>
        </Row>
        <Row className="col-6 pr-0">
          <MuteText>Tracking Number</MuteText>
          <span>{tracking.trackingNumber}</span>
        </Row>
        <Row className="col-6 pl-0">
          <MuteText>Carrier</MuteText>
          <span>{tracking.carrier?.toUpperCase()}</span>
        </Row>
      </div>

      <Form className="mb-4">
        <TextArea
          value={note}
          onChange={(_, data) => setNote(data.value as string)}
          placeholder="Add an optional note..."
        />
      </Form>

      <div className="d-flex justify-content-end">
        <HoverBorderButton onClick={onClose} disabled={loading}>
          Cancel
        </HoverBorderButton>
        <StyledButton className="ml-2" onClick={handleAcknowledge} loading={loading}>
          Acknowledge & Save
        </StyledButton>
      </div>
    </SkupremeModal>
  );
}
